import { useState } from 'react';
import SEOHead from '../../components/base/SEOHead';

interface APIEndpoint {
  method: string;
  endpoint: string;
  description: string;
  parameters?: { name: string; type: string; required: boolean; description: string }[];
  response: string;
  example: string;
}

const APIDocsPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const apiEndpoints: { [key: string]: APIEndpoint[] } = {
    auth: [
      {
        method: 'POST',
        endpoint: '/auth/signin',
        description: 'Authenticate user and get access token',
        parameters: [
          { name: 'email', type: 'string', required: true, description: 'User email address' },
          { name: 'password', type: 'string', required: true, description: 'User password' }
        ],
        response: '{ "access_token": "string", "user": { "id": "string", "email": "string" } }',
        example: `curl -X POST https://api.pepcoin.com/auth/signin \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com", "password": "password123"}'`
      },
      {
        method: 'POST',
        endpoint: '/auth/signup',
        description: 'Create new user account',
        parameters: [
          { name: 'email', type: 'string', required: true, description: 'User email address' },
          { name: 'password', type: 'string', required: true, description: 'User password' },
          { name: 'full_name', type: 'string', required: true, description: 'User full name' }
        ],
        response: '{ "user": { "id": "string", "email": "string", "full_name": "string" } }',
        example: `curl -X POST https://api.pepcoin.com/auth/signup \\
  -H "Content-Type: application/json" \\
  -d '{"email": "user@example.com", "password": "password123", "full_name": "John Doe"}'`
      }
    ],
    wallet: [
      {
        method: 'GET',
        endpoint: '/wallet/balance',
        description: 'Get user wallet balance',
        parameters: [
          { name: 'Authorization', type: 'header', required: true, description: 'Bearer token' }
        ],
        response: '{ "balance": "number", "currency": "string", "last_updated": "string" }',
        example: `curl -X GET https://api.pepcoin.com/wallet/balance \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      },
      {
        method: 'POST',
        endpoint: '/wallet/transfer',
        description: 'Transfer funds between wallets',
        parameters: [
          { name: 'to_address', type: 'string', required: true, description: 'Recipient wallet address' },
          { name: 'amount', type: 'number', required: true, description: 'Transfer amount' },
          { name: 'currency', type: 'string', required: true, description: 'Currency type (PEP, BTC, ETH)' }
        ],
        response: '{ "transaction_id": "string", "status": "string", "fee": "number" }',
        example: `curl -X POST https://api.pepcoin.com/wallet/transfer \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"to_address": "0x123...", "amount": 100, "currency": "PEP"}'`
      }
    ],
    mining: [
      {
        method: 'GET',
        endpoint: '/mining/stats',
        description: 'Get mining statistics and performance',
        parameters: [
          { name: 'Authorization', type: 'header', required: true, description: 'Bearer token' }
        ],
        response: '{ "hashrate": "number", "earnings": "number", "uptime": "number", "efficiency": "number" }',
        example: `curl -X GET https://api.pepcoin.com/mining/stats \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      },
      {
        method: 'POST',
        endpoint: '/mining/start',
        description: 'Start mining operation',
        parameters: [
          { name: 'plan_id', type: 'string', required: true, description: 'Mining plan identifier' },
          { name: 'cryptocurrency', type: 'string', required: true, description: 'Crypto to mine (BTC, ETH, etc.)' }
        ],
        response: '{ "session_id": "string", "status": "string", "estimated_earnings": "number" }',
        example: `curl -X POST https://api.pepcoin.com/mining/start \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"plan_id": "basic", "cryptocurrency": "BTC"}'`
      }
    ],
    trading: [
      {
        method: 'GET',
        endpoint: '/trading/robot/status',
        description: 'Get AI trading robot status',
        parameters: [
          { name: 'Authorization', type: 'header', required: true, description: 'Bearer token' }
        ],
        response: '{ "status": "string", "profit": "number", "trades_today": "number", "success_rate": "number" }',
        example: `curl -X GET https://api.pepcoin.com/trading/robot/status \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      },
      {
        method: 'POST',
        endpoint: '/trading/robot/configure',
        description: 'Configure AI trading robot settings',
        parameters: [
          { name: 'risk_level', type: 'string', required: true, description: 'Risk level (low, medium, high)' },
          { name: 'max_investment', type: 'number', required: true, description: 'Maximum investment amount' },
          { name: 'target_profit', type: 'number', required: false, description: 'Target profit percentage' }
        ],
        response: '{ "config_id": "string", "status": "string", "estimated_returns": "number" }',
        example: `curl -X POST https://api.pepcoin.com/trading/robot/configure \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"risk_level": "medium", "max_investment": 1000, "target_profit": 15}'`
      }
    ],
    p2p: [
      {
        method: 'GET',
        endpoint: '/p2p/orders',
        description: 'Get P2P trading orders',
        parameters: [
          { name: 'type', type: 'string', required: false, description: 'Order type (buy/sell)' },
          { name: 'currency', type: 'string', required: false, description: 'Currency filter' },
          { name: 'limit', type: 'number', required: false, description: 'Number of orders to return' }
        ],
        response: '{ "orders": [{ "id": "string", "type": "string", "amount": "number", "price": "number" }] }',
        example: `curl -X GET https://api.pepcoin.com/p2p/orders?type=buy&currency=BTC \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN"`
      },
      {
        method: 'POST',
        endpoint: '/p2p/orders/create',
        description: 'Create new P2P trading order',
        parameters: [
          { name: 'type', type: 'string', required: true, description: 'Order type (buy/sell)' },
          { name: 'amount', type: 'number', required: true, description: 'Order amount' },
          { name: 'price', type: 'number', required: true, description: 'Price per unit' },
          { name: 'currency', type: 'string', required: true, description: 'Currency type' }
        ],
        response: '{ "order_id": "string", "status": "string", "expires_at": "string" }',
        example: `curl -X POST https://api.pepcoin.com/p2p/orders/create \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "sell", "amount": 0.5, "price": 45000, "currency": "BTC"}'`
      }
    ]
  };

  const sections = [
    { id: 'overview', name: 'Overview', icon: 'ri-information-line' },
    { id: 'auth', name: 'Authentication', icon: 'ri-shield-user-line' },
    { id: 'wallet', name: 'Wallet', icon: 'ri-wallet-3-line' },
    { id: 'mining', name: 'Mining', icon: 'ri-cpu-line' },
    { id: 'trading', name: 'Trading', icon: 'ri-line-chart-line' },
    { id: 'p2p', name: 'P2P Trading', icon: 'ri-exchange-line' }
  ];

  const renderEndpoint = (endpoint: APIEndpoint) => (
    <div key={endpoint.endpoint} className="bg-gray-800/50 rounded-xl p-4 md:p-6 border border-gray-700">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-4">
        <span className={`px-3 py-1 rounded-lg text-sm font-bold w-fit ${
          endpoint.method === 'GET' ? 'bg-green-500/20 text-green-400' :
          endpoint.method === 'POST' ? 'bg-blue-500/20 text-blue-400' :
          endpoint.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-400' :
          'bg-red-500/20 text-red-400'
        }`}>
          {endpoint.method}
        </span>
        <code className="text-cyan-400 font-mono text-xs sm:text-sm break-all">{endpoint.endpoint}</code>
      </div>
      
      <p className="text-gray-300 mb-4 text-sm md:text-base">{endpoint.description}</p>
      
      {endpoint.parameters && (
        <div className="mb-4">
          <h4 className="text-white font-semibold mb-2 text-sm md:text-base">Parameters:</h4>
          <div className="space-y-3">
            {endpoint.parameters.map((param) => (
              <div key={param.name} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 bg-gray-900/30 p-3 rounded-lg">
                <code className="text-cyan-400 font-mono text-xs sm:text-sm">{param.name}</code>
                <span className={`px-2 py-1 rounded text-xs w-fit ${param.required ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>
                  {param.required ? 'required' : 'optional'}
                </span>
                <span className="text-purple-400 text-xs sm:text-sm">{param.type}</span>
                <span className="text-gray-400 text-xs sm:text-sm">{param.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="mb-4">
        <h4 className="text-white font-semibold mb-2 text-sm md:text-base">Response:</h4>
        <pre className="bg-gray-900 p-3 rounded-lg text-green-400 text-xs overflow-x-auto">
          {endpoint.response}
        </pre>
      </div>
      
      <div>
        <h4 className="text-white font-semibold mb-2 text-sm md:text-base">Example:</h4>
        <pre className="bg-gray-900 p-3 rounded-lg text-gray-300 text-xs overflow-x-auto">
          {endpoint.example}
        </pre>
      </div>
    </div>
  );

  return (
    <>
      <SEOHead 
        title="API Documentation - PepCoin Developer Resources"
        description="Complete API documentation for PepCoin platform. Access authentication, wallet, mining, trading, and P2P endpoints with examples and integration guides."
        keywords="PepCoin API, cryptocurrency API, blockchain API, trading API, mining API"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900/20 to-cyan-900/20">
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent mb-3 md:mb-4">
              API Documentation
            </h1>
            <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
              Complete developer resources for integrating with the PepCoin platform. 
              Build powerful applications with our comprehensive API endpoints.
            </p>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden fixed bottom-6 right-6 z-50 bg-gradient-to-r from-cyan-500 to-purple-500 text-white p-4 rounded-full shadow-lg cursor-pointer w-14 h-14 flex items-center justify-center"
          >
            <i className={`${isSidebarOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>

          <div className="grid lg:grid-cols-4 gap-6 md:gap-8">
            {/* Sidebar Navigation */}
            <div className={`lg:col-span-1 fixed lg:static inset-0 z-40 lg:z-auto transition-transform duration-300 ${
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
            }`}>
              {/* Mobile Overlay */}
              <div 
                className={`lg:hidden fixed inset-0 bg-black/50 ${isSidebarOpen ? 'block' : 'hidden'}`}
                onClick={() => setIsSidebarOpen(false)}
              ></div>
              
              <div className="relative lg:sticky top-0 lg:top-8 bg-gray-800/95 lg:bg-gray-800/50 rounded-xl p-4 md:p-6 border border-gray-700 h-full lg:h-auto overflow-y-auto max-w-xs lg:max-w-none mx-4 lg:mx-0 my-4 lg:my-0">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-base md:text-lg">Documentation</h3>
                  <button
                    onClick={() => setIsSidebarOpen(false)}
                    className="lg:hidden text-gray-400 hover:text-white cursor-pointer w-8 h-8 flex items-center justify-center"
                  >
                    <i className="ri-close-line text-xl"></i>
                  </button>
                </div>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => {
                        setActiveSection(section.id);
                        setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap text-sm md:text-base ${
                        activeSection === section.id
                          ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-cyan-400 border border-cyan-500/30'
                          : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                      }`}
                    >
                      <i className={`${section.icon} text-lg md:text-xl`}></i>
                      <span className="font-medium">{section.name}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              {activeSection === 'overview' && (
                <div className="space-y-6 md:space-y-8">
                  <div className="bg-gray-800/50 rounded-xl p-4 md:p-8 border border-gray-700">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">Getting Started</h2>
                    
                    <div className="space-y-4 md:space-y-6">
                      <div>
                        <h3 className="text-lg md:text-xl font-semibold text-cyan-400 mb-2 md:mb-3">Base URL</h3>
                        <pre className="bg-gray-900 p-3 md:p-4 rounded-lg text-green-400 text-xs md:text-sm overflow-x-auto">
                          https://api.pepcoin.com
                        </pre>
                      </div>
                      
                      <div>
                        <h3 className="text-lg md:text-xl font-semibold text-cyan-400 mb-2 md:mb-3">Authentication</h3>
                        <p className="text-gray-300 mb-3 text-sm md:text-base">
                          All API requests require authentication using Bearer tokens. Include your access token in the Authorization header:
                        </p>
                        <pre className="bg-gray-900 p-3 md:p-4 rounded-lg text-gray-300 text-xs md:text-sm overflow-x-auto">
                          Authorization: Bearer YOUR_ACCESS_TOKEN
                        </pre>
                      </div>
                      
                      <div>
                        <h3 className="text-lg md:text-xl font-semibold text-cyan-400 mb-2 md:mb-3">Rate Limits</h3>
                        <div className="grid sm:grid-cols-2 gap-3 md:gap-4">
                          <div className="bg-gray-900/50 p-3 md:p-4 rounded-lg">
                            <h4 className="text-white font-semibold mb-2 text-sm md:text-base">Standard Endpoints</h4>
                            <p className="text-gray-400 text-sm">1000 requests per hour</p>
                          </div>
                          <div className="bg-gray-900/50 p-3 md:p-4 rounded-lg">
                            <h4 className="text-white font-semibold mb-2 text-sm md:text-base">Trading Endpoints</h4>
                            <p className="text-gray-400 text-sm">5000 requests per hour</p>
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-lg md:text-xl font-semibold text-cyan-400 mb-2 md:mb-3">Response Format</h3>
                        <p className="text-gray-300 mb-3 text-sm md:text-base">All responses are returned in JSON format:</p>
                        <pre className="bg-gray-900 p-3 md:p-4 rounded-lg text-green-400 text-xs md:text-sm overflow-x-auto">
{`{
  "success": true,
  "data": { ... },
  "message": "Operation completed successfully",
  "timestamp": "2024-01-15T10:30:00Z"
}`}
                        </pre>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-800/50 rounded-xl p-4 md:p-8 border border-gray-700">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">Error Handling</h2>
                    
                    <div className="space-y-3 md:space-y-4">
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 md:p-4">
                        <h4 className="text-red-400 font-semibold mb-2 text-sm md:text-base">400 - Bad Request</h4>
                        <p className="text-gray-300 text-sm">Invalid request parameters or malformed JSON</p>
                      </div>
                      
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 md:p-4">
                        <h4 className="text-yellow-400 font-semibold mb-2 text-sm md:text-base">401 - Unauthorized</h4>
                        <p className="text-gray-300 text-sm">Invalid or missing authentication token</p>
                      </div>
                      
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 md:p-4">
                        <h4 className="text-purple-400 font-semibold mb-2 text-sm md:text-base">429 - Rate Limited</h4>
                        <p className="text-gray-300 text-sm">Too many requests, please slow down</p>
                      </div>
                      
                      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 md:p-4">
                        <h4 className="text-red-400 font-semibold mb-2 text-sm md:text-base">500 - Server Error</h4>
                        <p className="text-gray-300 text-sm">Internal server error, please try again later</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection !== 'overview' && apiEndpoints[activeSection] && (
                <div className="space-y-4 md:space-y-6">
                  <div className="bg-gray-800/50 rounded-xl p-4 md:p-8 border border-gray-700">
                    <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                      {sections.find(s => s.id === activeSection)?.name} API
                    </h2>
                    <p className="text-gray-400 text-sm md:text-base">
                      {activeSection === 'auth' && 'Manage user authentication and authorization'}
                      {activeSection === 'wallet' && 'Handle wallet operations and balance management'}
                      {activeSection === 'mining' && 'Control mining operations and monitor performance'}
                      {activeSection === 'trading' && 'Manage AI trading robot and trading strategies'}
                      {activeSection === 'p2p' && 'Create and manage peer-to-peer trading orders'}
                    </p>
                  </div>
                  
                  <div className="space-y-4 md:space-y-6">
                    {apiEndpoints[activeSection].map(renderEndpoint)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default APIDocsPage;
